import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';

import { Form, useCreateForm } from 'libs/forms';
import {
  CheckBoxField,
  FieldsGroup,
  FormThemeProvider,
  InputField,
  PasswordField,
  ServiceTypeSelectorField,
} from 'ui/ecosystems/forms';
import { Button } from 'ui';
import { publicApi } from 'api';

import { mediaPhone } from 'ui/styles/media';

import {
  AddressDetails,
  PlaceAutocompleteInput,
} from 'ui/ecosystems/forms/molecules/place-autocomplete/place-autocomplete-input';

import { FormFieldsBlock, TwoInputsGrid, Error } from '../atoms/forms';
import * as ServiceProviderSignUpForm from '../../forms/service-provider-signup';

export function SignUpServiceProviderForm() {
  const history = useHistory();
  const [error, setError] = useState(null);

  async function handleFormSubmit(values: ServiceProviderSignUpForm.FormState) {
    const data = ServiceProviderSignUpForm.format(values);
    try {
      await publicApi.post('/service-owner/register', data);
      history.push('/confirm-email/send');
    } catch (e) {
      const error = e.response?.data?.message ?? 'Something wrong';
      setError(error);
    }
  }

  const form = useCreateForm<ServiceProviderSignUpForm.FormState>(
    {
      onSubmit: handleFormSubmit,
    },
    [],
  );

  async function handleAutocompleteChange(value: AddressDetails) {
    console.log('value: ', value);
    form.FFFormApi.batch(() => {
      form.FFFormApi.change(
        'companyAddress',
        (value?.street_number || value?.route) &&
          [value?.street_number, value?.route].join(' '),
      );
      form.FFFormApi.change('city', value?.city);
      form.FFFormApi.change('state', value?.state);
      form.FFFormApi.change('zipcode', value?.zip_code);
      form.FFFormApi.change('latitude', value?.latitude);
      form.FFFormApi.change('longitude', value?.longitude);
    });
  }

  return (
    <Form
      onSubmit={handleFormSubmit}
      schema={ServiceProviderSignUpForm.schema}
      form={form}
      render={({ handleSubmit }) => (
        <FormThemeProvider middle>
          <FormRoot>
            <FormFields>
              <FormFieldsBlock>
                <InputField name="email" placeholder="Email" />
                <TwoFields>
                  <InputField name="firstName" placeholder="First Name" />
                  <InputField name="lastName" placeholder="Last Name" />
                </TwoFields>
                <InputField
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                />
              </FormFieldsBlock>
              <FormFieldsBlock>
                <InputField name="companyName" placeholder="Company Name" />
                <ServiceTypeSelectorField
                  name="companyTypeId"
                  placeholder="Select Type"
                />
              </FormFieldsBlock>
              <FormFieldsBlock>
                <FieldsGroup>
                  <PlaceAutocompleteInput
                    placeholder="Type your address here"
                    onChange={handleAutocompleteChange}
                  />
                  <InputField
                    name="companyAddress"
                    placeholder="Address"
                    disabled
                  />
                  <InputField name="city" placeholder="City" disabled />
                  <StateZipGroup>
                    <InputField name="state" placeholder="State" disabled />
                    <InputField name="zipcode" placeholder="Zip" disabled />
                  </StateZipGroup>
                </FieldsGroup>
              </FormFieldsBlock>
              <FormFieldsBlock>
                <PasswordField name="password" placeholder="Password" />
              </FormFieldsBlock>
            </FormFields>
            <Agreement>
              <CheckBoxField name="agree">
                <Message>
                  I agree &nbsp; <a href={'#'}> Terms of Service </a> and
                  <a href={'#'}> Privacy&nbsp;Policy </a>
                </Message>
              </CheckBoxField>
            </Agreement>
            {error && <Error>{error}</Error>}
            <SignUpButton medium onClick={handleSubmit}>
              Sign Up
            </SignUpButton>
          </FormRoot>
        </FormThemeProvider>
      )}
    />
  );
}

const TwoFields = styled(TwoInputsGrid)`
  ${mediaPhone} {
    grid-template-columns: 1fr;
    grid-gap: 16px;
  }
`;

const FormFields = styled.div`
  display: grid;
  grid-gap: 32px;
  ${mediaPhone} {
    grid-gap: 16px;
  }
`;

const StateZipGroup = styled(FieldsGroup)`
  grid-template-columns: 80fr 20fr;
`;

const Message = styled.div``;

const Agreement = styled.div`
  display: flex;
  justify-content: center;
  font-size: 14px;
  line-height: 21px;
`;

const SignUpButton = styled(Button)`
  width: 100%;
`;

const FormRoot = styled.div`
  ${Agreement} {
    margin-top: 34px;
    margin-bottom: 17px;
  }

  ${Error} {
    margin-bottom: 16px;
  }
`;
